import { z } from "zod";
import { localDateSchema, moneySchema, rateSchema, timestampSchema, uuidSchema, } from "../common/primitives.js";
/**
 * Vergi ve beyan takvimi (Prompt 11 §8; özellik bayrağı `EXPENSES_BUDGET`).
 *
 * Tarihler KODA GÖMÜLMEZ (AGENTS.md §3.12): platform şablonları → şirket kuralları (düzenlenebilir,
 * "doğrulanmalı" işaretli) → dönemlik yükümlülükler. Hafta sonu / resmî tatil kaydırması UYGULANMAZ
 * (mevzuat açık sorusu — kullanıcı yükümlülüğün tarihini elle düzeltebilir).
 */
const versionSchema = z.number().int().min(1).describe("İyimser kilit sürümü");
export const TAX_FREQUENCIES = ["MONTHLY", "QUARTERLY", "YEARLY"];
export const taxFrequencySchema = z.enum(TAX_FREQUENCIES);
export const TAX_FREQUENCY_LABELS = {
    MONTHLY: "Aylık",
    QUARTERLY: "Üç aylık",
    YEARLY: "Yıllık",
};
export const TAX_OBLIGATION_STATUSES = ["OPEN", "DONE", "CANCELLED"];
export const taxObligationStatusSchema = z.enum(TAX_OBLIGATION_STATUSES);
export const TAX_OBLIGATION_STATUS_LABELS = {
    OPEN: "Açık",
    DONE: "Tamamlandı",
    CANCELLED: "İptal edildi",
};
export const taxCalendarCodeSchema = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z][A-Z0-9_]{1,39}$/, "Kod büyük harf, rakam ve _ içerebilir (ör. VAT, MANUAL_SGK)");
/** Dönem anahtarı: `2026-08`, `2026-Q3` ya da `2026`. */
export const taxPeriodKeySchema = z
    .string()
    .regex(/^\d{4}(-\d{2}|-Q[1-4])?$/, "Dönem anahtarı 2026-08, 2026-Q3 ya da 2026 biçiminde olmalıdır");
const reminderDaysSchema = z
    .array(z.number().int().min(0).max(90))
    .max(10)
    .describe("Vadeye N gün kala hatırlatma eşikleri");
// ── Platform şablonları ──────────────────────────────────────────────────────
export const taxCalendarTemplateSchema = z.object({
    id: uuidSchema,
    code: z.string(),
    name: z.string(),
    frequency: taxFrequencySchema,
    dueDay: z.number().int().describe("Dönemi izleyen ayın günü (ayda o gün yoksa ayın son günü)"),
    dueMonthOffset: z.number().int().describe("Dönem sonundan kaç ay sonra"),
    isPayment: z.boolean().describe("Ödeme doğurur mu (Ba/Bs yalnızca bildirimdir)"),
    reminderDays: z.array(z.number().int()),
    description: z.string().nullable(),
    needsVerification: z.boolean().describe("Seed değeri; mevzuat / mali müşavir teyidi bekliyor"),
});
export const taxCalendarTemplateListSchema = z.object({ items: z.array(taxCalendarTemplateSchema) });
// ── Şirket kuralları ─────────────────────────────────────────────────────────
export const taxCalendarRuleSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    code: z.string(),
    name: z.string(),
    frequency: taxFrequencySchema,
    dueDay: z.number().int(),
    dueMonthOffset: z.number().int(),
    isPayment: z.boolean(),
    reminderDays: z.array(z.number().int()),
    needsVerification: z.boolean(),
    isActive: z.boolean(),
    /** Bu kuraldan üretilmiş, henüz kapatılmamış yükümlülük sayısı. */
    openObligationCount: z.number().int(),
    version: versionSchema,
});
export const taxCalendarRuleListSchema = z.object({ items: z.array(taxCalendarRuleSchema) });
export const listTaxCalendarRulesQuerySchema = z.object({
    companyId: uuidSchema,
    includeInactive: z
        .enum(["true", "false"])
        .default("false")
        .transform((value) => value === "true"),
});
export const createTaxCalendarRuleSchema = z.object({
    companyId: uuidSchema,
    code: taxCalendarCodeSchema,
    name: z.string().trim().min(1).max(150),
    frequency: taxFrequencySchema,
    dueDay: z.number().int().min(1).max(31),
    dueMonthOffset: z.number().int().min(0).max(12).default(1),
    isPayment: z.boolean().default(true),
    reminderDays: reminderDaysSchema.default([7, 3, 1]),
    needsVerification: z.boolean().default(false),
});
/** Kod ve sıklık DEĞİŞMEZ (üretilmiş yükümlülüklerin dönem anahtarları bunlara bağlıdır). */
export const updateTaxCalendarRuleSchema = z.object({
    name: z.string().trim().min(1).max(150).optional(),
    dueDay: z.number().int().min(1).max(31).optional(),
    dueMonthOffset: z.number().int().min(0).max(12).optional(),
    isPayment: z.boolean().optional(),
    reminderDays: reminderDaysSchema.optional(),
    needsVerification: z.boolean().optional(),
    isActive: z.boolean().optional(),
    version: versionSchema,
});
// ── Yükümlülükler ────────────────────────────────────────────────────────────
export const taxObligationSchema = z.object({
    id: uuidSchema,
    companyId: uuidSchema,
    ruleId: uuidSchema.nullable(),
    code: z.string(),
    periodKey: z.string(),
    title: z.string(),
    dueDate: localDateSchema,
    isPayment: z.boolean(),
    amount: moneySchema.nullable().describe("Kuruş; bilinmiyorsa null"),
    amountIsEstimate: z.boolean(),
    status: taxObligationStatusSchema,
    doneAt: timestampSchema.nullable(),
    treasuryTransactionId: uuidSchema.nullable(),
    remindedDays: z.array(z.number().int()),
    note: z.string().nullable(),
    /** Bugüne göre kalan gün (negatif = gecikmiş). */
    daysLeft: z.number().int(),
    version: versionSchema,
});
export const taxObligationListSchema = z.object({ items: z.array(taxObligationSchema) });
export const listTaxObligationsQuerySchema = z.object({
    companyId: uuidSchema,
    status: taxObligationStatusSchema.optional(),
    code: taxCalendarCodeSchema.optional(),
    dueFrom: localDateSchema.optional(),
    dueTo: localDateSchema.optional(),
    onlyPayment: z
        .enum(["true", "false"])
        .default("false")
        .transform((value) => value === "true"),
});
/** Elle yükümlülük (vergi / SGK ödemesi); kod serbesttir (ör. `MANUAL_SGK`). */
export const createTaxObligationSchema = z.object({
    companyId: uuidSchema,
    code: taxCalendarCodeSchema,
    periodKey: taxPeriodKeySchema,
    title: z.string().trim().min(1).max(200),
    dueDate: localDateSchema,
    isPayment: z.boolean().default(true),
    amount: moneySchema.nullable().default(null),
    amountIsEstimate: z.boolean().default(true),
    note: z.string().trim().max(500).nullable().default(null),
});
export const updateTaxObligationSchema = z.object({
    title: z.string().trim().min(1).max(200).optional(),
    dueDate: localDateSchema.optional(),
    amount: moneySchema.nullable().optional(),
    amountIsEstimate: z.boolean().optional(),
    note: z.string().trim().max(500).nullable().optional(),
    version: versionSchema,
});
/**
 * Tamamlandı işaretleme. `bankAccountId` verilirse AYNI işlemde banka çıkışı kaydedilir
 * (`TAXES_PAYABLE` borç / `BANK` alacak; fişi hazine keser).
 */
export const completeTaxObligationSchema = z.object({
    paidOn: localDateSchema.optional().describe("Verilmezse bugün (Europe/Istanbul)"),
    bankAccountId: uuidSchema.nullable().default(null),
    amount: moneySchema.nullable().default(null).describe("Ödeme tutarı; boşsa yükümlülüğün tutarı"),
    note: z.string().trim().max(500).nullable().default(null),
    version: versionSchema,
});
export const cancelTaxObligationSchema = z.object({
    reason: z.string().trim().min(3).max(500),
    version: versionSchema,
});
export const generateTaxObligationsSchema = z.object({
    companyId: uuidSchema,
    horizonDays: z.number().int().min(1).max(400).default(120),
});
export const taxCalendarRunResultSchema = z.object({
    rulesEnsured: z.number().int(),
    obligationsCreated: z.number().int(),
    remindersFired: z.number().int(),
    failed: z.array(z.object({ companyId: uuidSchema, error: z.string() })),
});
/**
 * `POST /tax-obligations/generate` yanıtı: ufuk içindeki TÜM yükümlülükler (`items`, idempotent koşuda da dolu)
 * ve bu koşunun ÜRETTİĞİ sayılar (`result`) — arayüz "kaç yeni yükümlülük üretildi" bilgisini buradan alır.
 * Tek şirket üzerinde çalışıldığı için `result.failed` her zaman boştur (hata istisna olarak döner).
 */
export const generateTaxObligationsResultSchema = taxObligationListSchema.extend({
    result: taxCalendarRunResultSchema,
});
// ── KDV özeti raporu ─────────────────────────────────────────────────────────
export const vatSummaryQuerySchema = z.object({
    companyId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
});
export const vatAccountRowSchema = z.object({
    accountCode: z.string(),
    accountName: z.string(),
    debit: moneySchema,
    credit: moneySchema,
    /** Hesaplanan KDV'de alacak − borç, indirilecek KDV'de borç − alacak (doğal yön). */
    net: moneySchema,
});
export const vatDocumentRowSchema = z.object({
    source: z.enum(["SALES_INVOICE", "PURCHASE_INVOICE", "EXPENSE"]),
    vatRateCode: z.string(),
    vatRate: rateSchema.nullable(),
    documentCount: z.number().int(),
    netAmount: moneySchema,
    vatAmount: moneySchema,
    withheldAmount: moneySchema,
});
export const VAT_SUMMARY_SOURCE_LABELS = {
    SALES_INVOICE: "Satış faturaları",
    PURCHASE_INVOICE: "Alış faturaları",
    EXPENSE: "Giderler",
};
export const vatSummarySchema = z.object({
    companyId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    /** 391.xx — hesaplanan KDV (defterden, POSTED fişler). */
    outputAccounts: z.array(vatAccountRowSchema),
    /** 191.xx — indirilecek KDV (defterden, POSTED fişler). */
    inputAccounts: z.array(vatAccountRowSchema),
    /** 360 — ödenecek vergi ve fonlar (tevkifat / stopaj); ayrı satır. */
    taxesPayableAccounts: z.array(vatAccountRowSchema),
    totalOutput: moneySchema,
    totalInput: moneySchema,
    /** Hesaplanan − indirilecek; pozitifse ödenecek, negatifse devreden KDV. */
    balance: moneySchema,
    payable: moneySchema,
    carriedForward: moneySchema,
    documents: z.array(vatDocumentRowSchema),
    disclaimer: z.string().describe("Bu rapor beyanname DEĞİLDİR"),
});
export const VAT_SUMMARY_DISCLAIMER = "Bu özet defter kayıtlarından ve belgelerden üretilen bir ÖN BİLGİDİR; KDV beyannamesi yerine geçmez. " +
    "Beyan öncesi mali müşavirinizle doğrulayın.";
//# sourceMappingURL=tax-calendar.js.map